import React, { lazy, Suspense, useEffect } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { Box, useModal } from "@getbread/dough";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material";
import { useRefreshTimer } from "@getbread/wheat-jwt-manager";
import type { FCWithChildren } from "../api-client/resources/types/sharedTypes";
import { Authenticated } from "../components/Authenticated";
import { useAuthentication } from "../components/AuthenticationProvider";
import { GenericErrorBoundary } from "../components/errors/GenericErrorBoundary";
import { Footer } from "../components/Footer/Footer";
import { Header } from "../components/Header/Header";
import { InvalidJwtErrorBoundary } from "../components/InvalidJwtErrorBoundary/InvalidJwtErrorBoundary";
import { PageContainer } from "../components/PageContainer/PageContainer";
import { PageHead } from "../components/PageHead/PageHead";
import { stdSpacing } from "../global.style";
import { refresh as refreshSession } from "../api-client/authentication";
import { jwtStorageKey } from "../apiClientProvider";

// Pages need to be default exports for code-splitting
const LoginPage = lazy(() => import("./LoginPage/LoginPage"));
const AccountPage = lazy(() => import("./AccountPage/AccountPage"));
const LoansPage = lazy(() => import("./LoansPage/LoansPage"));
const DisclosurePage = lazy(() => import("./DisclosurePage/DisclosurePage"));
const PaymentFullTermsPage = lazy(() => import("./DisclosurePage/PaymentFullTermsPage"));
const TermsAndConditionsPage = lazy(() => import("./DisclosurePage/RBCTermsAndConditionsPage"));

const Logout: React.FC = () => {
	const { logOut } = useAuthentication();
	const { destroyAllModals } = useModal();

	useEffect(() => {
		destroyAllModals();
		logOut();
	}, [destroyAllModals, logOut]);

	return <Navigate to="/" />;
};

const MainContent = styled("div")`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
`;

export const useRefreshTimerParams = (): Parameters<typeof useRefreshTimer>[0] => {
	const navigate = useNavigate();

	return {
		onSessionExpired: () => {
			navigate("/logout");
		},
		refreshSession,
		storageKey: jwtStorageKey,
		config: {
			// UNCOMMENT FOR TESTING
			// maxJwtValidityMs: 90 * 1000, // This will consider a JWT expired if it's older than 90 seconds
			// idleTimeoutMs: 40 * 1000, // This will consider you idle after 40 seconds of inactivity
			// maxSessionDurationMs: 300 * 60 * 1000, // Max log in time will be 5 minutes, then you're forced to log in again
		},
	} as const;
};

const FullRouteWrapper: FCWithChildren = ({ children }) => {
	return (
		<>
			<Header />
			<PageContainer>
				<GenericErrorBoundary>
					<InvalidJwtErrorBoundary>
						<Suspense
							fallback={
								<Box marginTop={stdSpacing.xxl}>
									<CircularProgress />
								</Box>
							}
						>
							<MainContent id="main-content">{children}</MainContent>
						</Suspense>
					</InvalidJwtErrorBoundary>
				</GenericErrorBoundary>
			</PageContainer>
			<Footer />
		</>
	);
};

const PartialRouteWrapper: FCWithChildren = ({ children }) => (
	<GenericErrorBoundary>
		<InvalidJwtErrorBoundary>
			<Suspense
				fallback={
					<Box marginTop={stdSpacing.xxl}>
						<CircularProgress />
					</Box>
				}
			>
				{children}
			</Suspense>
		</InvalidJwtErrorBoundary>
	</GenericErrorBoundary>
);

/** Backward compatibility so that /login path still resolves and redirects user to expected page */
const HandleLoginRouteRender: React.FC = () => {
	const { loggedIn } = useAuthentication();

	if (loggedIn) {
		return <Navigate to="/loans" />;
	}

	return <Navigate to="/" />;
};

export const PageRoutes: React.FC = () => {
	const params = useRefreshTimerParams();

	useRefreshTimer(params);

	return (
		<Routes>
			<Route
				path="/"
				element={
					<FullRouteWrapper>
						<LoginPage />
					</FullRouteWrapper>
				}
			/>
			<Route path="/login" element={<HandleLoginRouteRender />} />
			<Route path="/logout" element={<Logout />} />
			<Route
				path="/account" // In case anyone has a bookmark, need to redirect existing "account" requests to "my-information"
				element={<Navigate to="/my-information" replace />}
			/>
			<Route
				path="/my-information"
				element={
					<Authenticated>
						<FullRouteWrapper>
							<AccountPage />
						</FullRouteWrapper>
					</Authenticated>
				}
			/>
			<Route
				path="/loans/*"
				element={
					<Authenticated>
						<FullRouteWrapper>
							<LoansPage />
						</FullRouteWrapper>
					</Authenticated>
				}
			/>
			<Route
				path="/disclosure/payment-agreement/:loanId/views/:viewType"
				element={
					<Authenticated>
						<PartialRouteWrapper>
							<DisclosurePage />
						</PartialRouteWrapper>
					</Authenticated>
				}
			/>
			<Route
				path="/disclosure/payment-agreement/:loanId/acknowledgements/name/:viewType/target-id/:targetId"
				element={
					<Authenticated>
						<PartialRouteWrapper>
							<DisclosurePage />
						</PartialRouteWrapper>
					</Authenticated>
				}
			/>
			<Route
				path="/disclosure/payment-full-terms"
				element={
					<Authenticated>
						<PartialRouteWrapper>
							<PaymentFullTermsPage />
						</PartialRouteWrapper>
					</Authenticated>
				}
			/>
			<Route
				path="/disclosure/verification-service"
				element={
					<Authenticated>
						<PartialRouteWrapper>
							<TermsAndConditionsPage />
						</PartialRouteWrapper>
					</Authenticated>
				}
			/>
			<Route path="*" element={<Navigate to="/loans" />} />
		</Routes>
	);
};

/** Route definitions */
export const RootPage: React.FC = () => (
	<Router>
		<PageHead />
		<PageRoutes />
	</Router>
);
